import { forwardRef, useId } from "react";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select as MuiSelect,
} from "@mui/material";

import type {
  SelectProps as MuiSelectProps,
  MenuProps as MuiMenuProps,
} from "@mui/material";
import type { ReactNode } from "react";
import type { FormFieldSize } from "../../types";

export interface SelectProps extends Omit<
  MuiSelectProps,
  "variant" | "size" | "color" | "IconComponent" | "input" | "native"
> {
  /** Select size. Default: `"sm"` */
  size?: FormFieldSize;
  /** Field label, rendered via `InputLabel`. */
  label?: ReactNode;
  /** Applies the error border/label styling. */
  error?: boolean;
  /** Expands the control to its container width. */
  fullWidth?: boolean;
  /** Root test id. Default: `"ds-select"` */
  "data-testid"?: string;
}

const sizeMap: Record<FormFieldSize, "small" | "medium"> = {
  sm: "small",
  md: "medium",
};

const renderChips = (selected: unknown): ReactNode => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
    {(Array.isArray(selected) ? selected : [selected]).map((value) => (
      <Chip key={String(value)} label={String(value)} size="small" />
    ))}
  </Box>
);

/** Constrains MUI Select to the outlined Centro dropdown: locked variant/colour/icon, sm/md sizing, and chip multi-select. */
export const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      size = "sm",
      label,
      error,
      fullWidth,
      disabled,
      required,
      multiple,
      renderValue,
      MenuProps,
      id,
      labelId,
      "data-testid": dataTestId = "ds-select",
      children,
      ...rest
    },
    ref,
  ) => {
    const reactId = useId();
    const selectId = id ?? `${reactId}-select`;
    const resolvedLabelId = labelId ?? `${reactId}-label`;

    const menuProps: Partial<MuiMenuProps> = {
      ...MenuProps,
      PaperProps: { elevation: 8, ...MenuProps?.PaperProps },
    };

    return (
      <FormControl
        size={sizeMap[size]}
        error={error}
        fullWidth={fullWidth}
        disabled={disabled}
        required={required}
      >
        {label != null && <InputLabel id={resolvedLabelId}>{label}</InputLabel>}
        <MuiSelect
          {...rest}
          ref={ref}
          data-testid={dataTestId}
          variant="outlined"
          color="brandSupporting"
          id={selectId}
          labelId={label != null ? resolvedLabelId : labelId}
          label={label}
          multiple={multiple}
          renderValue={renderValue ?? (multiple ? renderChips : undefined)}
          MenuProps={menuProps}
        >
          {children}
        </MuiSelect>
      </FormControl>
    );
  },
);

Select.displayName = "Select";
